import React, { useRef } from 'react';
import { Button, Row } from 'reactstrap';
import { faArrowLeft, faFileCsv, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSVLink } from 'react-csv';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';

//Context
import { useAuth } from '../../../../context/auth';

//Components
import CardRelatorio from '../components/CardRelatorio';

//Helpers
import { formatarPeriodo } from '../../../../helpers/formats';
import { COM_INFRACAO } from '../../GerenciamentoDeFiscalizacao/helpers/gerenciamentoFiscalizacao';

//Estilo da pagina de impressão
const pageStyle = `
@media print {
  @page{
    size: A4 landscape;
    margin: 0.5rem;
  }

  body {
    -webkit-print-color-adjust: exact;
  }

  .relatorio {
    width: 100vw !important;
    display:flex !important;
    flex-direction: column !important;
    padding: 0px 15px !important;
  }

  div.header{
    display:table-header-group;
  }

  div.body{
    display:table-row-group;
  }

  .noPrint{
    display: none;
  }
}
`;

// CE-3641: histórico completo do auto de infração.
// CE-3912: o relatório acompanha o filtro Infração da tela — Com Infração traz o
// histórico dos autos, Sem Infração traz as fiscalizações que não geraram auto.
function RelatorioGerenciamentoFiscalizacao({ itens = [], filtros, handleVoltar }) {
  const { session, logoSistema, logoSecretaria, logoPrefeitura } = useAuth();

  //Referencia do relatorio para download do csv
  const exportRef = useRef(null);

  //Referencia para o componente de impressão
  const ref = useRef(null);

  const comInfracao = filtros.infracao === COM_INFRACAO;

  //headers da exportação do csv
  const headersExportComInfracao = [
    { key: "tipoFiscalizacao", label: "TIPO DE FISCALIZAÇÃO" },
    { key: "infracao", label: "INFRAÇÃO" },
    { key: "numeroInfracao", label: "NÚMERO DA INFRAÇÃO" },
    { key: "nomeInfrator", label: "NOME DO INFRATOR" },
    { key: "documentoInfrator", label: "CPF/CNPJ DO INFRATOR" },
    { key: "endereco", label: "ENDEREÇO DA INFRAÇÃO" },
    { key: "dataInfracao", label: "DATA DA INFRAÇÃO" },
    { key: "dataLavratura", label: "DATA DA LAVRATURA" },
    { key: "usuario", label: "USUÁRIO" },
    { key: "numeroProcesso", label: "PROCESSO" },
    { key: "situacao", label: "SITUAÇÃO" },
    { key: "statusBoleto", label: "STATUS DO BOLETO" },
    { key: "statusDividaAtiva", label: "STATUS DA DÍVIDA ATIVA" },
    { key: "dataPublicacaoNotificacao", label: "DATA PUBLICAÇÃO NOTIFICAÇÃO" },
    { key: "dataApresentacaoDefesa", label: "DATA APRESENTAÇÃO DEFESA" },
    { key: "dataIndeferimentoDefesa", label: "DATA INDEFERIMENTO DEFESA" },
    { key: "dataDeferimentoDefesa", label: "DATA DEFERIMENTO DEFESA" },
    { key: "dataApresentacaoPrimeiroRecurso", label: "DATA APRESENTAÇÃO 1º RECURSO" },
    { key: "dataIndeferimentoPrimeiroRecurso", label: "DATA INDEFERIMENTO 1º RECURSO" },
    { key: "dataDeferimentoPrimeiroRecurso", label: "DATA DEFERIMENTO 1º RECURSO" },
    { key: "dataApresentacaoSegundoRecurso", label: "DATA APRESENTAÇÃO 2º RECURSO" },
    { key: "dataIndeferimentoSegundoRecurso", label: "DATA INDEFERIMENTO 2º RECURSO" },
    { key: "dataDeferimentoSegundoRecurso", label: "DATA DEFERIMENTO 2º RECURSO" },
    { key: "dataEmissaoPrimeiroBoleto", label: "DATA EMISSÃO 1º BOLETO" },
    { key: "dataVencimentoPrimeiroBoleto", label: "VENCIMENTO 1º BOLETO" },
    { key: "valorPrimeiroBoleto", label: "VALOR DO 1º BOLETO" },
    { key: "dataEmissaoSegundaViaBoleto", label: "DATA EMISSÃO 2ª VIA BOLETO" },
    { key: "valorSegundaViaBoleto", label: "VALOR 2ª VIA BOLETO" },
    { key: "dataPagamentoPrimeiroBoleto", label: "DATA PAGAMENTO 1º BOLETO" },
    { key: "valorPagamentoPrimeiroBoleto", label: "VALOR PAGAMENTO 1º BOLETO" },
    { key: "dataPagamentoSegundaViaBoleto", label: "DATA PAGAMENTO 2ª VIA BOLETO" },
    { key: "valorPagamentoSegundaViaBoleto", label: "VALOR PAGAMENTO 2ª VIA BOLETO" },
  ];

  const headersExportSemInfracao = [
    { key: "tipoFiscalizacao", label: "TIPO DE FISCALIZAÇÃO" },
    { key: "nomeInfrator", label: "NOME DO INFRATOR" },
    { key: "documentoInfrator", label: "CPF/CNPJ DO INFRATOR" },
    { key: "endereco", label: "ENDEREÇO" },
    { key: "dataFiscalizacao", label: "DATA DA FISCALIZAÇÃO" },
    { key: "dataLavratura", label: "DATA DA LAVRATURA" },
    { key: "usuario", label: "USUÁRIO" },
    { key: "situacao", label: "SITUAÇÃO" },
  ];

  const headersRelatorioComInfracao = [
    //Infração ocupa a linha inteira: o texto é longo e, dividindo a linha com os
    //demais campos, sobra um bloco em branco ao lado do número da infração.
    { objectValue: "numeroInfracao", value: "Número da Infração", col: "4" },
    { objectValue: "tipoFiscalizacao", value: "Tipo de Fiscalização", col: "8" },
    { objectValue: "infracao", value: "Infração", col: "12" },
    { objectValue: "nomeInfrator", value: "Nome do Infrator", col: "6" },
    { objectValue: "documentoInfrator", value: "CPF/CNPJ do Infrator", col: "6" },
    { objectValue: "endereco", value: "Endereço da Infração", col: "12" },
    { objectValue: "dataInfracao", value: "Data da Infração", col: "3" },
    { objectValue: "dataLavratura", value: "Data da Lavratura", col: "3" },
    { objectValue: "usuario", value: "Usuário", col: "3" },
    { objectValue: "numeroProcesso", value: "Processo", col: "3" },
    { objectValue: "situacao", value: "Situação", col: "4" },
    { objectValue: "statusBoleto", value: "Status do Boleto", col: "4" },
    { objectValue: "statusDividaAtiva", value: "Status da Dívida Ativa", col: "4" },
    { objectValue: "dataPublicacaoNotificacao", value: "Publicação da Notificação", col: "3" },
    { objectValue: "dataApresentacaoDefesa", value: "Apresentação da Defesa", col: "3" },
    { objectValue: "dataIndeferimentoDefesa", value: "Indeferimento da Defesa", col: "3" },
    { objectValue: "dataDeferimentoDefesa", value: "Deferimento da Defesa", col: "3" },
    { objectValue: "dataApresentacaoPrimeiroRecurso", value: "Apresentação 1º Recurso", col: "4" },
    { objectValue: "dataIndeferimentoPrimeiroRecurso", value: "Indeferimento 1º Recurso", col: "4" },
    { objectValue: "dataDeferimentoPrimeiroRecurso", value: "Deferimento 1º Recurso", col: "4" },
    { objectValue: "dataApresentacaoSegundoRecurso", value: "Apresentação 2º Recurso", col: "4" },
    { objectValue: "dataIndeferimentoSegundoRecurso", value: "Indeferimento 2º Recurso", col: "4" },
    { objectValue: "dataDeferimentoSegundoRecurso", value: "Deferimento 2º Recurso", col: "4" },
    { objectValue: "dataEmissaoPrimeiroBoleto", value: "Emissão do 1º Boleto", col: "3" },
    { objectValue: "dataVencimentoPrimeiroBoleto", value: "Vencimento do 1º Boleto", col: "3" },
    { objectValue: "valorPrimeiroBoleto", value: "Valor do 1º Boleto", col: "3" },
    { objectValue: "dataEmissaoSegundaViaBoleto", value: "Emissão 2ª Via do Boleto", col: "3" },
    { objectValue: "valorSegundaViaBoleto", value: "Valor 2ª Via do Boleto", col: "3" },
    { objectValue: "dataPagamentoPrimeiroBoleto", value: "Pagamento 1º Boleto", col: "3" },
    { objectValue: "valorPagamentoPrimeiroBoleto", value: "Valor Pagamento 1º Boleto", col: "3" },
    { objectValue: "dataPagamentoSegundaViaBoleto", value: "Pagamento 2ª Via do Boleto", col: "3" },
    { objectValue: "valorPagamentoSegundaViaBoleto", value: "Valor Pagamento 2ª Via", col: "3" },
  ];

  //Fiscalização sem auto não tem boleto, dívida ativa, defesa nem recurso: o
  //relatório traz os mesmos campos da lista Sem Infração da tela.
  const headersRelatorioSemInfracao = [
    { objectValue: "tipoFiscalizacao", value: "Tipo de Fiscalização", col: "12" },
    { objectValue: "nomeInfrator", value: "Nome do Infrator", col: "6" },
    { objectValue: "documentoInfrator", value: "CPF/CNPJ do Infrator", col: "6" },
    { objectValue: "endereco", value: "Endereço", col: "12" },
    { objectValue: "dataFiscalizacao", value: "Data da Fiscalização", col: "3" },
    { objectValue: "dataLavratura", value: "Data da Lavratura", col: "3" },
    { objectValue: "usuario", value: "Usuário", col: "3" },
    { objectValue: "situacao", value: "Situação", col: "3" },
  ];

  const headersExport = comInfracao ? headersExportComInfracao : headersExportSemInfracao;
  const headersRelatorio = comInfracao ? headersRelatorioComInfracao : headersRelatorioSemInfracao;

  //Função acionada assim que o botão de imprimir pdf é clicado
  const handlePrint = useReactToPrint({
    content: () => ref.current,
    documentTitle: "Gerenciamento de Fiscalização",
    pageStyle: pageStyle
  });

  return <>
    <div className='shadowFicha mainContainerFicha p-4'>

      <div className='d-flex align-items-center justify-content-between'>
        <Button color='secondary' className='text-white' onClick={handleVoltar}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span className='px-2'> Voltar </span>
        </Button>
        <div className='d-flex gap-2'>
          <Button color='danger' onClick={handlePrint}>
            <FontAwesomeIcon icon={faFilePdf} /> Imprimir
          </Button>
          <Button color='success' onClick={() => exportRef.current.link.click()}>
            <FontAwesomeIcon icon={faFileCsv} />
            <span className='px-2'> CSV </span>
          </Button>
        </div>
      </div>

      <CSVLink
        separator={";"}
        data={itens}
        headers={headersExport}
        filename="Relatorio de Gerenciamento de Fiscalizacao.csv"
        target="_blank"
        ref={exportRef}
      />

      <div className="mt-4 relatorio w-100" ref={ref}>
        <div className="print m-2 d-flex align-items-center justify-content-between">
          <p>Emitido por: {session.nome}</p>
          <p>Data de emissão: {moment().format("DD/MM/YYYY HH:mm")}</p>
        </div>

        <div className='d-flex justify-content-between align-items-center w-100'>
          {logoSistema &&
            <div>
              <img src={logoSistema} width={180} alt="logo do sistema" />
            </div>
          }
          <div>
            {logoSecretaria && <img src={logoSecretaria} className='mt-3' width={140} alt="logo da secretaria" />}
            {logoSecretaria && <img src={logoPrefeitura} width={120} alt="logo da prefeitura" />}
          </div>
        </div>

        <div className="header">
          <h5 style={{ color: "#666" }}>Relatório Gerenciamento de Fiscalização</h5>
          <Row>
            <div className='col-12'>
              <p><span className="fw-bold">Período:</span> {formatarPeriodo(filtros.dataInicial, filtros.dataFinal)}</p>
            </div>
            <div className='col-6'>
              <p><span className="fw-bold">Tipo de Busca:</span> {filtros.tipoBusca}</p>
            </div>
            <div className='col-6'>
              <p><span className="fw-bold">Tipo de Fiscalização:</span> {filtros.tipoFiscalizacao_Descricao ?? "Todos"}</p>
            </div>
            <div className='col-6'>
              <p><span className="fw-bold">Infração:</span> {comInfracao ? "Com Infração" : "Sem Infração"}</p>
            </div>
            <div className='col-6'>
              <p>
                <span className="fw-bold">{comInfracao ? "Total de infrações:" : "Total de fiscalizações:"}</span> {itens.length}
              </p>
            </div>
          </Row>
        </div>

        <div
          className='body'
          style={{ color: "#666" }}
        >
          <h5 className='rounded-3 bg-secondary text-light p-2'>
            {comInfracao ? "Histórico das Infrações" : "Fiscalizações sem Infração"}
          </h5>
          <CardRelatorio headers={headersRelatorio} items={itens} />
        </div>
      </div>
    </div>
  </>;
}

export default RelatorioGerenciamentoFiscalizacao;
